/**
 * Tenant feature flags (Phase 3, F6).
 *
 * The backend resolves each tenant's flags and returns them as `features` on
 * GET /users/me/permissions. An older backend omits `features`; the frontend
 * then uses these defaults, which match the backend's.
 *
 * Default OFF: features the PRD excludes. The UI is hidden and the API returns 404.
 * Default ON: demo mocks a later phase finishes. They show with a Demo badge (none today).
 */

export const FEATURE_DEFAULTS = {
  oasis: false,
  medicareContent: false,
  evvFlagIntervention: false,
  billingReports: false,
  claimSubmissionPackage: false,
  // evvUsageScore and paEvvDrift were removed in Phase 10 (REQ-EVV-21 / REQ-PA-19 confirmed);
  // demoBillingRates in Phase 11 (rates come from the tenant billing configuration);
  // demoOrgAccount in Phase 13 (Org Account reads the real subscription)
  /** PAT-01 USPS address check on demographics save (Phase 6). OFF until counsel O-05 + USPS credentials. */
  uspsAddressVerify: false,
  /** Phase 13 (Q7): this tenant's money actions reach Stripe. OFF = ledger-only demo; Org Account reads `billingEnabled`. */
  subscriptionBilling: false,
  /** Phase 13: subscription emails are sent (OFF = logged as would_send). Backend-only; listed for parity. */
  subscriptionEmails: false,
  /** Phase 22: Pelora Billing (clearinghouse submission, worklist, batches). OFF = no EDI screen renders and no billing-edi request is sent. */
  billingEdi: false,
} as const;

export type FeatureKey = keyof typeof FEATURE_DEFAULTS;

export type FeatureFlags = Record<FeatureKey, boolean>;

export const FEATURE_KEYS = Object.keys(FEATURE_DEFAULTS) as FeatureKey[];

/**
 * Fill in any flag the backend did not send with its default.
 * Non-boolean values are ignored.
 */
export function resolveFeatures(features?: Record<string, unknown> | null): FeatureFlags {
  const resolved = { ...FEATURE_DEFAULTS } as FeatureFlags;
  if (!features) return resolved;
  for (const key of FEATURE_KEYS) {
    const value = features[key];
    if (typeof value === 'boolean') {
      resolved[key] = value;
    }
  }
  return resolved;
}
